import { ApolloCartErrorAlert, useFormGqlMutationCart } from '@graphcommerce/magento-cart'
import {
  AnimatedRow,
  Button,
  extendableComponent,
  iconChevronRight,
  MessageSnackbar,
  IconSvg,
} from '@graphcommerce/next-ui'
import { Trans } from '@lingui/react'
import { Divider, Typography, Alert, Box } from '@mui/material'
import { AnimatePresence } from 'framer-motion'
import PageLink from 'next/link'
import { useContext, useEffect, useLayoutEffect, useState } from 'react'
import { Selected, useConfigurableContext } from '@graphcommerce/magento-product-configurable/ConfigurableContext/ConfigurableContext'
import SelectQuantity from './SelectQuantity'
import { ConfigurableOptionsInput } from './ConfigurableOptionsInput'
import TemplateContext from '../TemplateContext/template-context'
import { useQuery } from '@graphcommerce/graphql'
import { AttributeTetheringDocument } from '../../../components/GraphCMS/AttributeTether/AttributeTether.gql'
import ProductContext from '../ProductContext/product-context'
import { Money } from '@graphcommerce/magento-store'
import cheapestVariant from '@graphcommerce/magento-product-configurable/ConfigurableContext/cheapestVariant'
import { ConfigurableProductAddToCartDocument } from '@graphcommerce/magento-product-configurable/graphql/ConfigurableProductAddToCart.gql'
import { AddProductsToCartButton } from '@graphcommerce/magento-product'
import { MaterialSelection } from './MaterialSelection'

const compName = 'ConfigurableOptionsInput' as const
const parts = ['form', 'button', 'finalPrice', 'quantity', 'divider', 'buttonWrapper'] as const
const { classes } = extendableComponent(compName, parts)

export function ConfigurableProductAddToCart(props) {
  const {
    name,
    sku,
    children,
    variables,
    optionEndLabels,
    optionsProps,
    additionalButtons,
    sx = [],
    setDisableButton,
    currentProduct,
    setCurrentProduct,
    product,
    setHasOne,
    isCustomizable,
    isUse,
    currentSku,
    setCurrentSku,
    selected,
    setSelected,
    ...buttonProps
  } = props

  const { quantity, chipValue } = useContext(TemplateContext);
  const { resetFilters } = useContext(ProductContext);

  const { getUids, getVariants, selection, select, variants: variants1, options } = useConfigurableContext(currentSku)

  const form = useFormGqlMutationCart(ConfigurableProductAddToCartDocument, {
    defaultValues: { ...variables },
    onBeforeSubmit: ({ selectedOptions, ...vars }) => ({
      ...vars,
      // todo: selectedOptions should contain the correct values directly
      selectedOptions: getUids(selectedOptions?.[0] as unknown as Selected),
    }),
  })

  const numberFormat = (value) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);

  const { formState, required, control, error, data } = form

  const overrides = useQuery(AttributeTetheringDocument, { variables: { url: 'product/global' } });
  let override_data: any = overrides?.data?.pages[0]?.content;
  const variants: any = getVariants(selection);
  const attributes = variants[0]?.attributes;
  const [overridesArray, setOverridesArray] = useState([]) as any[];
  override_data = override_data?.filter((content) => (content?.__typename === "AttributeTether"));
  override_data = override_data?.filter((content) => (content?.sku === currentSku));

  useLayoutEffect(() => {
    if (overrides.loading) {
      return;
    }
    if (variants) {
      for (let variant of variants) { //Iterate through all possible variants of selection.
        if (variant?.attributes) {
          for (let attribute of variant.attributes) { //Iterate through every attribute of the variant.
            for (let content of override_data) { //Iterate through the CMS map.
              if (attribute && attribute?.code) {
                if ((attribute.code === content.attributeName && attribute.uid === content.uid)) {//Match the code to the CMS attribute name and the uid to the CMS uid.
                  for (let attribute2 of variant.attributes) { //Iterate through every other attribute per the variants.
                    if (attribute2?.code) {
                      if (attribute2.code === content.connectedName && attribute2.uid === content.connectedUid) {//Match the connectedName to the other attribute's code and the connectedUid to the other attribute's UID.
                        setOverridesArray((prev: any) => [...prev, { [attribute.code]: attribute.uid, [attribute2.code]: attribute2.uid }]);
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }, []); //This code is executed during static generation

  useLayoutEffect(() => {
    if (Object.keys(selection).find(key => key === "edge_pattern") && !Object.keys(selection).find(key => key === "color")) {
      if (attributes.length > 1) {
        select((prev) => (findOtherAttribute(prev, 'color', 'edge_pattern') as Selected | any));
        resetFilters();
        setHasOne((prev) => !prev);
      }
    } else if (!Object.keys(selection).find(key => key === "edge_pattern") && Object.keys(selection).find(key => key === "color")) {
      if (attributes.length > 1) {
        select((prev) => (findOtherAttribute(prev, 'edge_pattern', 'color') as Selected | any));
        resetFilters();
        setHasOne((prev) => !prev);
      }
    }

    if (Object.keys(selection).length == 0) {
      setDisableButton(true);
      resetFilters();
    } else {
      setCurrentProduct({ ...getVariants(selection)[0]?.product });
      setDisableButton(false);
      resetFilters();
    }

  }, [selection, overrides]);

  function findOtherAttribute(prev, code, current) {
    let att = {};
    if (overridesArray.length > 0) {
      for (let override of overridesArray) {
        if (override && override[current] === selection[Object.keys(selection)[0]]) {
          return ({ ...prev, [code]: override[code] })
        }
      }
    }
    if (attributes) {
      for (let attribute of attributes) {
        if (attribute) {
          if (attribute.code === code && attribute.code) {
            att = { [attribute.code]: attribute.uid };
          }
        }
      }
    }
    return { ...prev, ...att };
  }


  return (<>
    <Box
      component='form'
      noValidate
      className={classes.form}
      sx={[
        {
          width: '100%',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {isUse &&
        <MaterialSelection
          setCurrentSku={setCurrentSku}
          currentSku={currentSku}
          setSelected={setSelected}
          name="materialSelection"
        />}
      {((isUse && selected) || !isUse) &&
        <>
          <Divider className={classes.divider} />
          <ConfigurableOptionsInput
            name='selectedOptions'
            sku={currentSku}
            control={control}
            rules={{ required: required.selectedOptions }}
            errors={formState.errors}
            optionEndLabels={optionEndLabels}
            {...optionsProps}
          />
        </>}

      <Divider className={classes.divider} sx={(theme) => ({ margin: `${theme.spacings.sm} 0` })} />
      <SelectQuantity sx={sx} price_tiers={currentProduct?.price_tiers.length > 0 ? currentProduct?.price_tiers : product?.price_tiers} optionEndLabels={optionEndLabels} />
      <Typography
        component='div'
        variant='h3'
        className={classes.finalPrice}
        sx={(theme) => ({ marginY: theme.spacings.sm })}
      >
        {!isCustomizable ? <Money
          {...cheapestVariant(getVariants(selection))?.product?.price_range?.minimum_price
            .final_price ?? 0}
        /> :
          numberFormat(quantity * chipValue)}
      </Typography>
      {children}
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          columnGap: theme.spacings.xs,
        })}
        className={classes.buttonWrapper}
      >
      </Box>

      <ApolloCartErrorAlert error={error} />

      <AnimatePresence initial={false}>
        {data?.addProductsToCart?.user_errors.map((e) => (
          <AnimatedRow key={e?.code}>
            <Alert severity='error'>{e?.message}</Alert>
          </AnimatedRow>
        ))}
      </AnimatePresence>

      <MessageSnackbar
        open={
          !formState.isSubmitting &&
          formState.isSubmitSuccessful &&
          !error?.message &&
          !data?.addProductsToCart?.user_errors?.length
        }
        variant='pill'
        action={
          <PageLink href='/cart' passHref>
            <Button
              id='view-shopping-cart-button'
              size='medium'
              variant='pill'
              color='secondary'
              endIcon={<IconSvg src={iconChevronRight} />}
            >
              <Trans id='View shopping cart' />
            </Button>
          </PageLink>
        }
      >
        <Trans
          id='<0>{name}</0> has been added to your shopping cart!'
          components={{ 0: <strong /> }}
          values={{ name }}
        />
      </MessageSnackbar>
    </Box>

  </>
  )
}
